import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import { downloadCsv, downloadPdf, datePresets } from "./financeV2Export";
import "./FinanceV2.css";

const defaultRange = datePresets.mtd();

export default function FinanceV2Reports() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [tab, setTab] = useState("pnl");
  const [rows, setRows] = useState([]);
  const [tbTotals, setTbTotals] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = { from, to, branch_id };
    try {
      if (tab === "trial") {
        const res = await FinanceV2Services.getTrialBalance(params);
        const payload = res.data?.data;
        const lines = Array.isArray(payload) ? payload : payload?.lines || [];
        setRows(lines);
        setTbTotals(Array.isArray(payload) ? null : payload?.totals || null);
        setPnl(null);
        setBs(null);
      } else if (tab === "pnl") {
        const res = await FinanceV2Services.getProfitAndLoss(params);
        setPnl(res.data?.data);
        setRows([]);
        setBs(null);
      } else {
        const res = await FinanceV2Services.getBalanceSheet({ as_of: to, branch_id });
        setBs(res.data?.data);
        setRows([]);
        setPnl(null);
      }
    } catch {
      setRows([]);
      setPnl(null);
      setBs(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tab, branch_id]);

  const applyPreset = (key) => {
    const p = datePresets[key]();
    setFrom(p.from);
    setTo(p.to);
  };

  const exportTrialCsv = () => {
    downloadCsv(
      `trial-balance-${from}-${to}.csv`,
      ["Code", "Account", "Type", "Debit", "Credit", "Balance"],
      rows.map((r) => [r.code, r.name, r.type, r.debit, r.credit, r.balance])
    );
  };

  const exportTrialPdf = () => {
    downloadPdf(
      `Trial Balance ${from} to ${to}`,
      ["Code", "Account", "Type", "Debit", "Credit", "Balance"],
      rows.map((r) => [r.code, r.name, r.type, r.debit, r.credit, r.balance]),
      `trial-balance-${from}-${to}.pdf`
    );
  };

  const exportPnlCsv = () => {
    if (!pnl) return;
    const lines = [
      ...pnl.income_lines.map((r) => ["Income", r.code, r.name, "", "", r.balance]),
      ["", "", "Total income", "", "", pnl.total_income],
      ...pnl.expense_lines.map((r) => ["Expense", r.code, r.name, "", "", r.balance]),
      ["", "", "Total expense", "", "", pnl.total_expense],
      ["", "", "Net income", "", "", pnl.net_income],
    ];
    downloadCsv(`pnl-${from}-${to}.csv`, ["Section", "Code", "Account", "Debit", "Credit", "Amount"], lines);
  };

  const exportPnlPdf = () => {
    if (!pnl) return;
    const body = [
      ...pnl.income_lines.map((r) => ["Income", r.code, r.name, r.balance]),
      ["", "", "Total income", pnl.total_income],
      ...pnl.expense_lines.map((r) => ["Expense", r.code, r.name, r.balance]),
      ["", "", "Total expense", pnl.total_expense],
      ["", "", "Net income", pnl.net_income],
    ];
    downloadPdf(`Profit & Loss ${from} to ${to}`, ["Type", "Code", "Account", "Amount"], body, `pnl-${from}-${to}.pdf`);
  };

  const exportBsCsv = () => {
    if (!bs) return;
    const lines = [
      ...bs.assets.map((r) => ["Asset", r.code, r.name, r.balance]),
      ["", "", "Total assets", bs.total_assets],
      ...bs.liabilities.map((r) => ["Liability", r.code, r.name, r.balance]),
      ["", "", "Total liabilities", bs.total_liabilities],
      ...bs.equity.map((r) => ["Equity", r.code, r.name, r.balance]),
      ["", "", "Total equity", bs.total_equity],
    ];
    downloadCsv(`balance-sheet-${to}.csv`, ["Section", "Code", "Account", "Balance"], lines);
  };

  const exportBsPdf = () => {
    if (!bs) return;
    const body = [
      ...bs.assets.map((r) => ["Asset", r.code, r.name, r.balance]),
      ...bs.liabilities.map((r) => ["Liability", r.code, r.name, r.balance]),
      ...bs.equity.map((r) => ["Equity", r.code, r.name, r.balance]),
    ];
    downloadPdf(`Balance Sheet as of ${to}`, ["Section", "Code", "Account", "Balance"], body, `balance-sheet-${to}.pdf`);
  };

  const handleExport = (fmt) => {
    if (tab === "trial") fmt === "csv" ? exportTrialCsv() : exportTrialPdf();
    else if (tab === "pnl") fmt === "csv" ? exportPnlCsv() : exportPnlPdf();
    else fmt === "csv" ? exportBsCsv() : exportBsPdf();
  };

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
          <h4 className="mb-0">Finance V2 — Reports</h4>
          <div className="d-flex gap-2 flex-wrap">
            <Link to="/finance-v2/general-ledger" className="btn btn-sm btn-outline-primary">
              General ledger
            </Link>
            <Link to="/finance-v2/mappings" className="btn btn-sm btn-outline-primary">
              Mappings & vendors
            </Link>
            <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
              Dashboard
            </Link>
          </div>
        </div>

        <div className="fv2-card">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-auto">
              <label className="form-label small">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <label className="form-label small">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="col-auto d-flex gap-1 flex-wrap">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("mtd")}>
                MTD
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("ytd")}>
                YTD
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("last30")}>
                Last 30 days
              </button>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-sm btn-success" onClick={load} disabled={loading}>
                {loading ? "Loading…" : "Run report"}
              </button>
            </div>
            <div className="col-auto ms-auto d-flex gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => handleExport("csv")}
                disabled={loading}
              >
                Export CSV
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => handleExport("pdf")}
                disabled={loading}
              >
                Export PDF
              </button>
            </div>
          </div>

          <div className="btn-group mb-3">
            {["pnl", "trial", "balance"].map((t) => (
              <button
                key={t}
                type="button"
                className={`btn btn-sm ${tab === t ? "btn-success" : "btn-outline-secondary"}`}
                onClick={() => setTab(t)}
              >
                {t === "pnl" ? "P&L" : t === "trial" ? "Trial balance" : "Balance sheet"}
              </button>
            ))}
          </div>

          {tab === "trial" && (
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th className="text-end">Debit</th>
                  <th className="text-end">Credit</th>
                  <th className="text-end">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.account_id}>
                    <td>{r.code}</td>
                    <td>{r.name}</td>
                    <td>{r.type}</td>
                    <td className="text-end">{r.debit}</td>
                    <td className="text-end">{r.credit}</td>
                    <td className="text-end">{r.balance}</td>
                  </tr>
                ))}
                {!rows.length && !loading && (
                  <tr>
                    <td colSpan={6} className="text-muted text-center">
                      No data — adjust dates and run report.
                    </td>
                  </tr>
                )}
              </tbody>
              {tbTotals && (
                <tfoot>
                  <tr className="fw-bold table-light">
                    <td colSpan={3}>Totals (books)</td>
                    <td className="text-end">{tbTotals.debit}</td>
                    <td className="text-end">{tbTotals.credit}</td>
                    <td className="text-end">
                      {tbTotals.balanced ? (
                        <span className="text-success">Balanced</span>
                      ) : (
                        <span className="text-danger">Off by {tbTotals.difference}</span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {tab === "pnl" && pnl && (
            <>
              <h6>Income</h6>
              <ReportLines lines={pnl.income_lines} />
              <p className="fw-bold">Total income: {pnl.total_income}</p>
              <h6 className="mt-3">Expenses</h6>
              <ReportLines lines={pnl.expense_lines} />
              <p className="fw-bold">Total expenses: {pnl.total_expense}</p>
              <p className="fw-bold text-success">Net income: {pnl.net_income}</p>
            </>
          )}
          {tab === "pnl" && !pnl && !loading && <p className="text-muted">Run report to load P&L.</p>}

          {tab === "balance" && bs && (
            <>
              <p className="small text-muted">As of {to}</p>
              <h6>Assets — Total: {bs.total_assets}</h6>
              <ReportLines lines={bs.assets} />
              <h6 className="mt-2">Liabilities — Total: {bs.total_liabilities}</h6>
              <ReportLines lines={bs.liabilities} />
              <h6 className="mt-2">Equity — Total: {bs.total_equity}</h6>
              <ReportLines lines={bs.equity} />
            </>
          )}
          {tab === "balance" && !bs && !loading && <p className="text-muted">Run report to load balance sheet.</p>}
        </div>
      </div>
    </FinanceV2Layout>
  );
}

function ReportLines({ lines }) {
  if (!lines?.length) return <p className="text-muted small">No data</p>;
  return (
    <table className="table table-sm">
      <tbody>
        {lines.map((r) => (
          <tr key={r.account_id}>
            <td>{r.code}</td>
            <td>{r.name}</td>
            <td className="text-end">{r.balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
